import { UserType } from '@enums/index';
import { showToast } from '@slices/apiSlice';
import { baseApi } from '@store/baseQuery';

import {
    AchievementPayload,
    AchievementResponse,
    AchievementsResponse,
    DeleteResponse,
    ExperiencePayload,
    ExperienceResponse,
    ExperiencesResponse,
    ProfessionalInfoPayload,
} from '../types';

export const professionalInfo = baseApi.injectEndpoints({
    endpoints: builder => ({
        updateMentorProfessionalInfo: builder.mutation<
            { message: string },
            ProfessionalInfoPayload
        >({
            query: body => ({
                url: `/mentor/profession-info`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Personal'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Professional Info updated successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),

        updateMenteeProfessionalInfo: builder.mutation<
            { message: string },
            ProfessionalInfoPayload
        >({
            query: body => ({
                url: `/mentee/profession-info`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Personal'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Professional Info updated successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),

        addMentorExperience: builder.mutation<ExperienceResponse, ExperiencePayload>({
            query: body => ({
                url: '/mentor/experience',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Experience'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Experience added successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        addMenteeExperience: builder.mutation<ExperienceResponse, ExperiencePayload>({
            query: body => ({
                url: '/mentee/experience',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Experience'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Experience added successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        getMentorExperience: builder.query<ExperiencesResponse, { username: string }>({
            query: args => ({
                url: `/mentor/${args.username}/experience?offset=0&limit=100&orderBy=startDate&orderDirection=DESC`,
                method: 'GET',
            }),
            providesTags: ['Experience'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        getMenteeExperience: builder.query<ExperiencesResponse, { username: string }>({
            query: args => ({
                url: `/mentee/${args.username}/experience?offset=0&limit=100&orderBy=startDate&orderDirection=DESC`,
                method: 'GET',
            }),
            providesTags: ['Experience'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        updateMentorExperience: builder.mutation<
            ExperienceResponse,
            { body: ExperiencePayload; experienceId: string }
        >({
            query: args => ({
                url: `/mentor/experience/${args.experienceId}`,
                method: 'PUT',
                body: args.body,
            }),
            invalidatesTags: ['Experience'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Experience updated successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        updateMenteeExperience: builder.mutation<
            ExperienceResponse,
            { body: ExperiencePayload; experienceId: string }
        >({
            query: args => ({
                url: `/mentee/experience/${args.experienceId}`,
                method: 'PUT',
                body: args.body,
            }),
            invalidatesTags: ['Experience'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Experience updated successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        deleteMentorExperience: builder.mutation<DeleteResponse, { experienceId: string }>({
            query: args => ({
                url: `/mentor/experience/${args.experienceId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Experience'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Experience deleted successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        deleteMenteeExperience: builder.mutation<DeleteResponse, { experienceId: string }>({
            query: args => ({
                url: `/mentee/experience/${args.experienceId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Experience'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Experience deleted successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),

        addMentorAchievement: builder.mutation<AchievementResponse, AchievementPayload>({
            query: body => ({
                url: '/mentor/achievement',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Achievement'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Achievement added successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        addMenteeAchievement: builder.mutation<AchievementResponse, AchievementPayload>({
            query: body => ({
                url: '/mentee/achievement',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Achievement'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Achievement added successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        getMentorAchievements: builder.query<AchievementsResponse, { username: string }>({
            query: args => ({
                url: `/mentor/${args.username}/achievement?offset=0&limit=100&orderBy=createdAt&orderDirection=ASC`,
                method: 'GET',
            }),
            providesTags: ['Achievement'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        getMenteeAchievements: builder.query<AchievementsResponse, { username: string }>({
            query: args => ({
                url: `/mentee/${args.username}/achievement?offset=0&limit=100&orderBy=createdAt&orderDirection=ASC`,
                method: 'GET',
            }),
            providesTags: ['Achievement'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        updateMentorAchievement: builder.mutation<
            AchievementResponse,
            { body: AchievementPayload; achievementId: string }
        >({
            query: args => ({
                url: `/mentor/achievement/${args.achievementId}`,
                method: 'PUT',
                body: args.body,
            }),
            invalidatesTags: ['Achievement'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Achievement updated successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        updateMenteeAchievement: builder.mutation<
            AchievementResponse,
            { body: AchievementPayload; achievementId: string }
        >({
            query: args => ({
                url: `/mentee/achievement/${args.achievementId}`,
                method: 'PUT',
                body: args.body,
            }),
            invalidatesTags: ['Achievement'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Achievement updated successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        deleteMentorAchievement: builder.mutation<DeleteResponse, { achievementId: string }>({
            query: args => ({
                url: `/mentor/achievement/${args.achievementId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Achievement'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Achievement deleted successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        bulkUpsertAchievement: builder.mutation<
            AchievementsResponse,
            { userType: UserType; data: AchievementPayload[] }
        >({
            query: ({ userType, data }) => ({
                url: `/${userType}/achievement/upsert/bulk`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Achievement'],
            async onQueryStarted({ userType, data }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Achievements updated successfully',
                        })
                    );
                } catch (error: any) {
                    dispatch(
                        showToast({
                            variant: 'error',
                            description: error?.error || 'Something went wrong',
                        })
                    );
                }
            },
        }),
        deleteMenteeAchievement: builder.mutation<DeleteResponse, { achievementId: string }>({
            query: args => ({
                url: `/mentee/achievement/${args.achievementId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Achievement'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Achievement deleted successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
    }),
    overrideExisting: true,
});

export const {
    useAddMentorExperienceMutation,
    useAddMenteeExperienceMutation,
    useLazyGetMentorExperienceQuery,
    useLazyGetMenteeExperienceQuery,
    useUpdateMentorExperienceMutation,
    useUpdateMenteeExperienceMutation,
    useDeleteMentorExperienceMutation,
    useDeleteMenteeExperienceMutation,
    useUpdateMentorProfessionalInfoMutation,
    useUpdateMenteeProfessionalInfoMutation,
    useAddMentorAchievementMutation,
    useAddMenteeAchievementMutation,
    useLazyGetMentorAchievementsQuery,
    useLazyGetMenteeAchievementsQuery,
    useUpdateMentorAchievementMutation,
    useUpdateMenteeAchievementMutation,
    useDeleteMentorAchievementMutation,
    useDeleteMenteeAchievementMutation,
    // useBulkUpsertMenteeAchievementMutation,
    // useBulkUpsertMentorAchievementMutation,
    useBulkUpsertAchievementMutation,
} = professionalInfo;
